import {IROIWidgetConfig} from "../models/roi-widget.props";
import {ACTIVITY_TYPES} from "../constants";

const PlatformSdk = require("@gluenetworks/platform-sdk-remote");
const ActivitySummarizationEntity = PlatformSdk.entities.ws.ActivitySummarization;
const Work = PlatformSdk.entities.ws.Work;

export interface IActivitySummarization {
    _id: string;
    tid: string;
    day: number; // Unix timestamp
    summarization: {
        totalCount?: number;
        totalTimeSaved?: number;
        totalCostSaved?: number;
        [activityType: string]: unknown;
    };
}

interface IDailyData {
    day: string;
    count: number;
    timeSaved: number;
    costSaved: number;
}

interface IActivitySummaryData {
    count?: number;
    timeSaved?: number;
    costSaved?: number;
}

interface IActivityData {
    dailyDetails?: IDailyData[];
    [key: string]: unknown;
}

export interface IROICalculationResult {
    roi: number;
    roiPercentage: string;
    totalInvestment: number;
    totalReturn: number;
    netProfit: number;
    totalSavings: number;
    totalActivities: number;
    totalHoursSaved: number;
    totalCostSaved: number;
    activityBreakdown: {
        [activityType: string]: {
            count: number;
            hoursPerTask: number;
            totalHours: number;
            totalSavings: number;
        };
    };
    calculationMethod: string;
    timeframe: string;
    currency: string;
    // Daily data for charts
    dailyActivities?: IDailyData[];
    // Raw response data for top automated tasks
    rawData?: {
        report?: {
            activities?: {[key: string]: any};
        };
        roiConfig?: {
            activities?: {[key: string]: {hoursPerTask: number}};
        };
    };
    // Comparison data
    comparison?: {
        enabled: boolean;
        type: "previous_period" | "same_period_last_year";
        previousData?: {
            totalHoursSaved: number;
            totalCostSaved: number;
            totalActivities: number;
            dailyActivities?: IDailyData[];
        };
        changes?: {
            hoursSavedChange: number;
            hoursSavedPercentage: string;
            costSavedChange: number;
            costSavedPercentage: string;
            activitiesChange: number;
            activitiesPercentage: string;
        };
    };
}

export class ROIDataService {
    /**
     * Fetch ActivitySummarization data using the platform SDK entity runRoiReportWork method
     */
    static async fetchActivitySummarization(
        tenantId: string,
        fromDate: Date,
        toDate: Date,
        config?: any,
        filters?: {
            activityTypes?: string[];
            siteIds?: string[];
            actionGroupIds?: string[];
        }
    ): Promise<IActivitySummarization[]> {
        try {
            // Use runRoiReportWork method as specified in backend document
            const workTarget = {
                category: "entity",
                entityType: "ActivitySummarization",
                method: "single",
            };

            // Build activities only from selected and configured tasks (second tab - Task Configuration)
            const activities: any = {};

            if (config?.taskConfiguration && Array.isArray(config.taskConfiguration)) {
                config.taskConfiguration.forEach((task: any) => {
                    // Only include tasks that are selected (enabled) and have timePerTask configured
                    if (
                        task.id &&
                        task.timePerTask !== undefined &&
                        task.timePerTask !== null &&
                        task.timePerTask > 0 &&
                        task.enabled === true
                    ) {
                        // Only include explicitly enabled tasks
                        activities[task.id] = {
                            hoursPerTask: task.timePerTask,
                        };
                    }
                });
            }

            // If no activities are selected/configured, return empty result or throw error
            if (Object.keys(activities).length === 0) {
                console.warn("No activities selected or configured for ROI calculation");
                return []; // Return empty array instead of throwing error
            }

            // Get dynamic hourlyRate and currencyCode from config, fallback to hardcoded
            let hourlyRate = 49.99;
            let currencyCode = "USD";

            // Try to get from globalSettings (first tab)
            // Note: averageHourlyRate might be 0, so check for !== undefined instead of truthy
            if (
                config?.globalSettings?.averageHourlyRate !== undefined &&
                config?.globalSettings?.averageHourlyRate !== null
            ) {
                hourlyRate = config.globalSettings.averageHourlyRate;
            }

            if (config?.globalSettings?.currency?.value) {
                currencyCode = config.globalSettings.currency.value;
            }

            // Build filter object from provided filters
            const filterObject: any = {};
            if (filters?.siteIds && filters.siteIds.length > 0) {
                filterObject.siteIds = filters.siteIds;
            }
            if (filters?.actionGroupIds && filters.actionGroupIds.length > 0) {
                filterObject.actionGroupIds = filters.actionGroupIds;
            }

            // activityTypes should contain the keys from the roiConfig activities
            // This tells the backend which activity types to count in the ROI calculation
            const activityKeys = Object.keys(activities);
            if (activityKeys.length > 0) {
                filterObject.activityTypes = activityKeys;
            }

            const workInput = {
                fromDate: fromDate.getTime(),
                toDate: toDate.getTime(),
                roiConfig: {
                    hourlyRate: hourlyRate,
                    currencyCode: currencyCode,
                    activities: activities,
                },
                filter: filterObject,
                outputs: {
                    jsonReport: true,
                    csvReport: true,
                    pdfReport: true,
                    dailyDetails: true,
                },
            };

            const workResult = await ActivitySummarizationEntity.runRoiReportWorkWork(
                tenantId,
                workTarget,
                workInput,
                {} // options
            );

            if (workResult.errorMsg) {
                throw new Error(`ROI Report work failed: ${workResult.errorMsg}`);
            }

            // Extract the report data from the work result
            const reportData = workResult.summary?.report;

            if (!reportData) {
                console.warn("No report data found in work result");
                return [];
            }

            // Convert the backend report structure to our expected format
            const results: IActivitySummarization[] = [];

            // Process daily details from allActivities
            if (reportData.allActivities?.dailyDetails) {
                reportData.allActivities.dailyDetails.forEach((dailyData: IDailyData) => {
                    results.push({
                        _id: `daily-${dailyData.day}`,
                        tid: tenantId,
                        day: new Date(dailyData.day).getTime(),
                        summarization: {
                            // Store the aggregated data for this day
                            totalCount: dailyData.count,
                            totalTimeSaved: dailyData.timeSaved,
                            totalCostSaved: dailyData.costSaved,
                            // Store individual activity data
                            ...Object.keys(reportData.activities || {}).reduce(
                                (acc: Record<string, unknown>, activityType: string) => {
                                    const activityData = reportData.activities[
                                        activityType
                                    ] as IActivityData;
                                    const dailyActivityData = activityData.dailyDetails?.find(
                                        (d: IDailyData) => d.day === dailyData.day
                                    );
                                    if (dailyActivityData) {
                                        acc[activityType] = {
                                            count: dailyActivityData.count,
                                            timeSaved: dailyActivityData.timeSaved,
                                            costSaved: dailyActivityData.costSaved,
                                        };
                                    }
                                    return acc;
                                },
                                {}
                            ),
                        },
                    });
                });
            }

            return results;
        } catch (error) {
            console.error("Error fetching ActivitySummarization data:", error);
            throw error;
        }
    }

    /**
     * Fetch ActivitySummarization data with raw response for top automated tasks
     */
    static async fetchActivitySummarizationWithRaw(
        tenantId: string,
        fromDate: Date,
        toDate: Date,
        config: IROIWidgetConfig,
        filters?: any
    ): Promise<{data: IActivitySummarization[]; rawData: any}> {
        try {
            // Build activities only from selected and configured tasks (second tab - Task Configuration)
            const activities: any = {};

            if (config?.taskConfiguration && Array.isArray(config.taskConfiguration)) {
                config.taskConfiguration.forEach((task: any) => {
                    // Only include tasks that are selected (enabled) and have timePerTask configured
                    if (
                        task.id &&
                        task.timePerTask !== undefined &&
                        task.timePerTask !== null &&
                        task.timePerTask > 0 &&
                        task.enabled === true
                    ) {
                        // Only include explicitly enabled tasks
                        activities[task.id] = {
                            hoursPerTask: task.timePerTask,
                        };
                    }
                });
            }

            // If no activities are selected/configured, return empty result or throw error
            if (Object.keys(activities).length === 0) {
                console.warn("No activities selected or configured for ROI calculation");
                return {data: [], rawData: null}; // Return empty result instead of throwing error
            }

            // Get dynamic hourlyRate and currencyCode from config, fallback to hardcoded
            let hourlyRate = 49.99;
            let currencyCode = "USD";

            // Try to get from globalSettings (first tab)
            // Note: averageHourlyRate might be 0, so check for !== undefined instead of truthy
            if (
                config?.globalSettings?.averageHourlyRate !== undefined &&
                config?.globalSettings?.averageHourlyRate !== null
            ) {
                hourlyRate = config.globalSettings.averageHourlyRate;
            }

            if (config?.globalSettings?.currency?.value) {
                currencyCode = config.globalSettings.currency.value;
            }

            // Build filter object
            const filterObject: any = {};
            if (filters?.siteIds && filters.siteIds.length > 0) {
                filterObject.siteIds = filters.siteIds;
            }
            if (filters?.actionGroupIds && filters.actionGroupIds.length > 0) {
                filterObject.actionGroupIds = filters.actionGroupIds;
            }
            if (filters?.activityTypes && filters.activityTypes.length > 0) {
                filterObject.activityTypes = filters.activityTypes;
            }

            const workInput = {
                fromDate: fromDate.getTime(),
                toDate: toDate.getTime(),
                roiConfig: {
                    hourlyRate: hourlyRate,
                    currencyCode: currencyCode,
                    activities: activities,
                },
                filter: filterObject,
                outputs: {
                    jsonReport: true,
                    csvReport: true,
                    pdfReport: true,
                    dailyDetails: true,
                },
            };

            const workTarget = {
                category: "entity",
                entityType: "ActivitySummarization",
                method: "single",
            };

            const workResult = await ActivitySummarizationEntity.runRoiReportWorkWork(
                tenantId,
                workTarget,
                workInput,
                {}
            );

            if (workResult.errorMsg) {
                throw new Error(`ROI Report work failed: ${workResult.errorMsg}`);
            }

            const reportData = workResult.summary?.report;
            if (!reportData) {
                console.warn("No report data found in work result");
                return {data: [], rawData: null};
            }

            // Process the data (same logic as fetchActivitySummarization)
            const results: IActivitySummarization[] = [];

            if (reportData.allActivities?.dailyDetails) {
                reportData.allActivities.dailyDetails.forEach((dailyData: IDailyData) => {
                    results.push({
                        _id: `daily-${dailyData.day}`,
                        tid: tenantId,
                        day: new Date(dailyData.day).getTime(),
                        summarization: {
                            totalCount: dailyData.count,
                            totalTimeSaved: dailyData.timeSaved,
                            totalCostSaved: dailyData.costSaved,
                            ...Object.keys(reportData.activities || {}).reduce(
                                (acc: Record<string, unknown>, activityType: string) => {
                                    const activityData = reportData.activities[
                                        activityType
                                    ] as IActivityData;
                                    const dailyActivityData = activityData.dailyDetails?.find(
                                        (d: IDailyData) => d.day === dailyData.day
                                    );
                                    if (dailyActivityData) {
                                        acc[activityType] = {
                                            count: dailyActivityData.count,
                                            timeSaved: dailyActivityData.timeSaved,
                                            costSaved: dailyActivityData.costSaved,
                                        };
                                    }
                                    return acc;
                                },
                                {}
                            ),
                        },
                    });
                });
            }

            // Return both processed data and raw data
            return {
                data: results,
                rawData: {
                    report: reportData,
                    roiConfig: workInput.roiConfig,
                },
            };
        } catch (error) {
            console.error("Error fetching ActivitySummarization data with raw:", error);
            throw error;
        }
    }

    /**
     * Calculate ROI metrics from backend report data
     */
    static calculateROI(
        data: IActivitySummarization[],
        config: IROIWidgetConfig,
        rawData?: any
    ): IROICalculationResult {
        const currencyCode = config?.globalSettings?.currency?.value || "USD";

        // Initialize totals
        let totalActivities = 0;
        let totalHoursSaved = 0;
        let totalCostSaved = 0;
        const activityBreakdown: {
            [key: string]: {
                count: number;
                hoursPerTask: number;
                totalHours: number;
                totalSavings: number;
            };
        } = {};

        // Initialize daily activities array for charts
        const dailyActivities: IDailyData[] = [];

        // Process the backend report data
        data.forEach((dayData) => {
            const summarization = dayData.summarization || {};

            // Use the totals from backend if available
            const dayCount = summarization.totalCount || 0;
            const dayTimeSaved = summarization.totalTimeSaved || 0;
            let dayCostSaved = summarization.totalCostSaved || 0;

            // Calculate cost saved if backend doesn't provide it or it's 0
            const hourlyRate = config?.globalSettings?.averageHourlyRate || 0;
            if (dayCostSaved === 0 && dayTimeSaved > 0 && hourlyRate > 0) {
                dayCostSaved = dayTimeSaved * hourlyRate;
            }

            totalActivities += dayCount;
            totalHoursSaved += dayTimeSaved;
            totalCostSaved += dayCostSaved;

            // Add daily data for charts
            dailyActivities.push({
                day: new Date(dayData.day).toISOString().split("T")[0], // Convert timestamp to date string
                count: dayCount,
                timeSaved: dayTimeSaved,
                costSaved: dayCostSaved,
            });

            // Process individual activity breakdowns
            Object.keys(summarization).forEach((activityType) => {
                if (activityType.startsWith("total")) return; // Skip total fields

                const activityData = summarization[activityType] as IActivitySummaryData;
                if (activityData && typeof activityData === "object") {
                    const count = activityData.count || 0;
                    const timeSaved = activityData.timeSaved || 0;
                    const costSaved = activityData.costSaved || 0;

                    // Update activity breakdown
                    if (!activityBreakdown[activityType]) {
                        activityBreakdown[activityType] = {
                            count: 0,
                            hoursPerTask: 0,
                            totalHours: 0,
                            totalSavings: 0,
                        };
                    }

                    activityBreakdown[activityType].count += count;
                    activityBreakdown[activityType].totalHours += timeSaved;
                    activityBreakdown[activityType].totalSavings += costSaved;
                    activityBreakdown[activityType].hoursPerTask =
                        activityBreakdown[activityType].count > 0
                            ? activityBreakdown[activityType].totalHours /
                              activityBreakdown[activityType].count
                            : 0;
                }
            });
        });

        // Calculate ROI percentage (assuming investment is the cost of Gluware)
        const totalInvestment = 10000; // Default investment
        const netProfit = totalCostSaved - totalInvestment;
        const roiPercentage = totalInvestment > 0 ? (netProfit / totalInvestment) * 100 : 0;

        // Format timeframe
        const timeframe = `${data.length} days`;

        return {
            roi: roiPercentage,
            roiPercentage: `${roiPercentage.toFixed(1)}%`,
            totalInvestment,
            totalReturn: totalCostSaved,
            netProfit,
            totalSavings: totalCostSaved,
            totalActivities,
            totalHoursSaved,
            totalCostSaved,
            activityBreakdown,
            calculationMethod: "Cost Savings",
            timeframe,
            currency: currencyCode,
            dailyActivities, // Add daily data for charts
            rawData, // Include raw backend response for top automated tasks
        };
    }

    /**
     * Get default date range (last 60 days)
     */
    static getDefaultDateRange(): {fromDate: Date; toDate: Date} {
        const toDate = new Date();
        const fromDate = new Date();
        fromDate.setDate(fromDate.getDate() - 60);

        // Round down to start of day for fromDate
        fromDate.setHours(0, 0, 0, 0);

        // Round up to end of day for toDate
        toDate.setHours(23, 59, 59, 999);

        return {fromDate, toDate};
    }

    /**
     * Download ROI report with configured activities and format
     */
    static async downloadComprehensiveReport(
        tenantId: string,
        fromDate: Date,
        toDate: Date,
        config: IROIWidgetConfig,
        filters?: {
            siteIds?: string[];
            actionGroupIds?: string[];
        }
    ): Promise<void> {
        try {
            // Build activities from config (Task Configuration)
            const activities: any = {};

            // Get activities from config (second tab - Task Configuration)
            if (config?.taskConfiguration && Array.isArray(config.taskConfiguration)) {
                config.taskConfiguration.forEach((task: any) => {
                    if (
                        task.id &&
                        task.timePerTask !== undefined &&
                        task.timePerTask !== null &&
                        task.timePerTask > 0 &&
                        task.enabled === true
                    ) {
                        activities[task.id] = {
                            hoursPerTask: task.timePerTask,
                        };
                    }
                });
            }

            // If no activities are selected/configured, throw error
            if (Object.keys(activities).length === 0) {
                throw new Error(
                    "No activities selected or configured for ROI report download. Please configure task activities in the widget settings."
                );
            }

            // Get dynamic hourlyRate and currencyCode from config
            let hourlyRate = 49.99;
            let currencyCode = "USD";

            if (
                config?.globalSettings?.averageHourlyRate !== undefined &&
                config?.globalSettings?.averageHourlyRate !== null
            ) {
                hourlyRate = config.globalSettings.averageHourlyRate;
            }

            if (config?.globalSettings?.currency?.value) {
                currencyCode = config.globalSettings.currency.value;
            }

            // Build filter object
            const filterObject: any = {};
            if (filters?.siteIds && filters.siteIds.length > 0) {
                filterObject.siteIds = filters.siteIds;
            }
            if (filters?.actionGroupIds && filters.actionGroupIds.length > 0) {
                filterObject.actionGroupIds = filters.actionGroupIds;
            }

            // Include all activity types for comprehensive report
            const activityKeys = Object.keys(activities);
            if (activityKeys.length > 0) {
                filterObject.activityTypes = activityKeys;
            }

            // Get report format from config (default to PDF if not specified)
            const reportFormat = config?.reportFormat?.value || "pdf";

            const workInput = {
                fromDate: fromDate.getTime(),
                toDate: toDate.getTime(),
                roiConfig: {
                    hourlyRate: hourlyRate,
                    currencyCode: currencyCode,
                    activities: activities,
                },
                filter: filterObject,
                outputs: {
                    jsonReport: reportFormat === "json",
                    csvReport: reportFormat === "csv",
                    pdfReport: reportFormat === "pdf",
                    dailyDetails: true,
                },
            };

            const workTarget = {
                category: "entity",
                entityType: "ActivitySummarization",
                method: "single",
            };

            const workResult = await ActivitySummarizationEntity.runRoiReportWorkWork(
                tenantId,
                workTarget,
                workInput,
                {}
            );

            if (workResult.errorMsg) {
                throw new Error(`ROI Report download failed: ${workResult.errorMsg}`);
            }

            // Load the work entity to get report files
            const workEntity = await Work.loadWork(tenantId, workResult.id);

            // Get the reports created by the work
            const reportEntities = await workEntity.loadRelatedReports();

            if (!reportEntities || reportEntities.length === 0) {
                throw new Error("No reports found in work result");
            }

            // Download each report type
            for (const reportEntity of reportEntities) {
                const {reportType, name, report} = reportEntity;
                const reportName = name.replace(/\//g, "-");
                const timestamp = new Date().toISOString().split("T")[0];

                let downloadUrl: string;
                let filename: string;
                let blob: Blob;

                switch (reportType) {
                    case "json":
                        filename = `roi-report-${timestamp}-${reportName}.json`;
                        blob = new Blob([JSON.stringify(report, null, 2)], {
                            type: "application/json",
                        });
                        downloadUrl = URL.createObjectURL(blob);
                        break;
                    case "csv":
                        filename = `roi-report-${timestamp}-${reportName}.csv`;
                        blob = new Blob([report], {type: "text/csv"});
                        downloadUrl = URL.createObjectURL(blob);
                        break;
                    case "pdf": {
                        filename = `roi-report-${timestamp}-${reportName}.pdf`;
                        const pdfBuffer = Buffer.from(report, "base64");
                        blob = new Blob([pdfBuffer], {type: "application/pdf"});
                        downloadUrl = URL.createObjectURL(blob);
                        break;
                    }
                    default:
                        console.warn(`Unknown report type: ${reportType}`);
                        continue;
                }

                // Trigger download
                const link = document.createElement("a");
                link.href = downloadUrl;
                link.download = filename;
                link.target = "_blank";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                // Clean up the blob URL
                setTimeout(() => URL.revokeObjectURL(downloadUrl), 100);
            }
        } catch (error) {
            console.error("Error downloading comprehensive ROI report:", error);
            throw error;
        }
    }

    /**
     * Download ROI report in specified format (legacy method)
     */
    static async downloadReport(
        tenantId: string,
        fromDate: Date,
        toDate: Date,
        config: IROIWidgetConfig,
        format: "pdf" | "csv",
        filters?: {
            siteIds?: string[];
            actionGroupIds?: string[];
        }
    ): Promise<void> {
        try {
            // Build activities only from selected and configured tasks
            const activities: any = {};

            if (config?.taskConfiguration && Array.isArray(config.taskConfiguration)) {
                config.taskConfiguration.forEach((task: any) => {
                    if (
                        task.id &&
                        task.timePerTask !== undefined &&
                        task.timePerTask !== null &&
                        task.timePerTask > 0 &&
                        task.enabled === true
                    ) {
                        activities[task.id] = {
                            hoursPerTask: task.timePerTask,
                        };
                    }
                });
            }

            // If no activities are selected/configured, throw error
            if (Object.keys(activities).length === 0) {
                throw new Error("No activities selected or configured for ROI report");
            }

            // Get dynamic hourlyRate and currencyCode from config
            let hourlyRate = 49.99;
            let currencyCode = "USD";

            if (
                config?.globalSettings?.averageHourlyRate !== undefined &&
                config?.globalSettings?.averageHourlyRate !== null
            ) {
                hourlyRate = config.globalSettings.averageHourlyRate;
            }

            if (config?.globalSettings?.currency?.value) {
                currencyCode = config.globalSettings.currency.value;
            }

            // Build filter object
            const filterObject: any = {};
            if (filters?.siteIds && filters.siteIds.length > 0) {
                filterObject.siteIds = filters.siteIds;
            }
            if (filters?.actionGroupIds && filters.actionGroupIds.length > 0) {
                filterObject.actionGroupIds = filters.actionGroupIds;
            }

            // activityTypes should contain the keys from the roiConfig activities
            const activityKeys = Object.keys(activities);
            if (activityKeys.length > 0) {
                filterObject.activityTypes = activityKeys;
            }

            const workInput = {
                fromDate: fromDate.getTime(),
                toDate: toDate.getTime(),
                roiConfig: {
                    hourlyRate: hourlyRate,
                    currencyCode: currencyCode,
                    activities: activities,
                },
                filter: filterObject,
                outputs: {
                    jsonReport: true,
                    csvReport: true,
                    pdfReport: true,
                    dailyDetails: true,
                },
            };

            const workTarget = {
                category: "entity",
                entityType: "ActivitySummarization",
                method: "single",
            };

            const workResult = await ActivitySummarizationEntity.runRoiReportWorkWork(
                tenantId,
                workTarget,
                workInput,
                {}
            );

            if (workResult.errorMsg) {
                throw new Error(`ROI Report download failed: ${workResult.errorMsg}`);
            }

            // Extract the download URL from the work result
            const reportData = workResult.summary?.report;
            if (!reportData) {
                throw new Error("No report data found in download result");
            }

            let downloadUrl: string | null = null;
            let filename = `roi-report-${new Date().toISOString().split("T")[0]}`;

            if (format === "pdf" && reportData.pdfReport) {
                downloadUrl = reportData.pdfReport;
                filename += ".pdf";
            } else if (format === "csv" && reportData.csvReport) {
                downloadUrl = reportData.csvReport;
                filename += ".csv";
            }

            if (!downloadUrl) {
                throw new Error(`${format.toUpperCase()} report not available in response`);
            }

            // Trigger download
            const link = document.createElement("a");
            link.href = downloadUrl;
            link.download = filename;
            link.target = "_blank";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error("Error downloading ROI report:", error);
            throw error;
        }
    }

    /**
     * Get activity display name
     */
    static getActivityDisplayName(activityType: string): string {
        const displayNames: {[key: string]: string} = {
            [ACTIVITY_TYPES.CAPTURE]: "Device Discovery",
            [ACTIVITY_TYPES.FEATURE_PROVISIONING]: "Config Deployment",
            [ACTIVITY_TYPES.OS_UPGRADE]: "OS Updates",
            [ACTIVITY_TYPES.AUDIT]: "Compliance Check",
            [ACTIVITY_TYPES.BACKUP]: "Backup Management",
            [ACTIVITY_TYPES.INVENTORY_REPORT]: "Inventory Reports",
        };

        // Handle workflow activities
        if (activityType.startsWith(ACTIVITY_TYPES.WORKFLOW_PREFIX)) {
            const workflowName = activityType.split(":")[1] || "Unknown";
            return `Workflow: ${workflowName}`;
        }

        return displayNames[activityType] || activityType;
    }

    /**
     * Calculate comparison date range based on comparison type
     */
    static getComparisonDateRange(
        fromDate: Date,
        toDate: Date,
        comparisonType: "previous_period" | "same_period_last_year"
    ): {fromDate: Date; toDate: Date} {
        const rangeDuration = toDate.getTime() - fromDate.getTime();

        if (comparisonType === "previous_period") {
            // Previous period: same duration before the current period
            const comparisonToDate = new Date(fromDate.getTime() - 1); // End just before current period starts
            const comparisonFromDate = new Date(comparisonToDate.getTime() - rangeDuration);

            return {
                fromDate: comparisonFromDate,
                toDate: comparisonToDate,
            };
        } else {
            // Same period last year
            const comparisonFromDate = new Date(fromDate);
            const comparisonToDate = new Date(toDate);

            comparisonFromDate.setFullYear(comparisonFromDate.getFullYear() - 1);
            comparisonToDate.setFullYear(comparisonToDate.getFullYear() - 1);

            return {
                fromDate: comparisonFromDate,
                toDate: comparisonToDate,
            };
        }
    }

    /**
     * Calculate percentage change between current and previous values
     */
    static calculatePercentageChange(current: number, previous: number): string {
        if (previous === 0) {
            return current > 0 ? "+100%" : "0%";
        }

        const change = ((current - previous) / previous) * 100;
        const sign = change >= 0 ? "+" : "";
        return `${sign}${change.toFixed(1)}%`;
    }

    /**
     * Calculate absolute change between current and previous values
     */
    static calculateAbsoluteChange(current: number, previous: number): number {
        return current - previous;
    }

    /**
     * Fetch ROI data with comparison support
     */
    static async fetchROIDataWithComparison(
        tenantId: string,
        fromDate: Date,
        toDate: Date,
        config: IROIWidgetConfig,
        filters?: {
            siteIds?: string[];
            actionGroupIds?: string[];
        }
    ): Promise<IROICalculationResult> {
        // Fetch current period data
        // Check if we need raw data for top automated tasks
        const needsRawData = config.metric?.value === "top_automated_tasks";

        let currentResult: IROICalculationResult;
        if (needsRawData) {
            const {data: currentData, rawData} = await this.fetchActivitySummarizationWithRaw(
                tenantId,
                fromDate,
                toDate,
                config,
                filters
            );
            currentResult = this.calculateROI(currentData, config, rawData);
        } else {
            const currentData = await this.fetchActivitySummarization(
                tenantId,
                fromDate,
                toDate,
                config,
                filters
            );
            currentResult = this.calculateROI(currentData, config);
        }

        // If comparison is not enabled, return current data only

        if (!config.compareTo?.enabled || !config.compareTo?.option?.value) {
            return currentResult;
        }

        try {
            // Calculate comparison date range
            const comparisonType = config.compareTo.option.value as
                | "previous_period"
                | "same_period_last_year";
            const comparisonRange = this.getComparisonDateRange(fromDate, toDate, comparisonType);

            // Fetch comparison period data
            const comparisonData = await this.fetchActivitySummarization(
                tenantId,
                comparisonRange.fromDate,
                comparisonRange.toDate,
                config,
                filters
            );
            const comparisonResult = this.calculateROI(comparisonData, config);

            // Calculate changes
            const hoursSavedChange = this.calculateAbsoluteChange(
                currentResult.totalHoursSaved,
                comparisonResult.totalHoursSaved
            );
            const costSavedChange = this.calculateAbsoluteChange(
                currentResult.totalCostSaved,
                comparisonResult.totalCostSaved
            );
            const activitiesChange = this.calculateAbsoluteChange(
                currentResult.totalActivities,
                comparisonResult.totalActivities
            );

            // Add comparison data to result
            currentResult.comparison = {
                enabled: true,
                type: comparisonType,
                previousData: {
                    totalHoursSaved: comparisonResult.totalHoursSaved,
                    totalCostSaved: comparisonResult.totalCostSaved,
                    totalActivities: comparisonResult.totalActivities,
                    dailyActivities: comparisonResult.dailyActivities,
                },
                changes: {
                    hoursSavedChange,
                    hoursSavedPercentage: this.calculatePercentageChange(
                        currentResult.totalHoursSaved,
                        comparisonResult.totalHoursSaved
                    ),
                    costSavedChange,
                    costSavedPercentage: this.calculatePercentageChange(
                        currentResult.totalCostSaved,
                        comparisonResult.totalCostSaved
                    ),
                    activitiesChange,
                    activitiesPercentage: this.calculatePercentageChange(
                        currentResult.totalActivities,
                        comparisonResult.totalActivities
                    ),
                },
            };

            return currentResult;
        } catch (error) {
            console.error("Error fetching comparison data:", error);
            // Return current data without comparison if comparison fails
            return currentResult;
        }
    }
}
